import axiosInstance from "./axiosConfig";

// API endpoint for products
const PRODUCTS_URL = "/products";

/**
 * Get all products
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.category - Filter by category ID
 * @param {string} params.search - Search term
 * @param {string} params.sortBy - Sort field
 * @param {string} params.sortOrder - Sort order (asc/desc)
 * @param {number} params.minPrice - Minimum price filter
 * @param {number} params.maxPrice - Maximum price filter
 * @param {string} params.attributes - JSON string of attribute filters
 * @param {boolean} params.detailed - Whether to include detailed data
 * @returns {Object} Products data with pagination
 */
const getProducts = async (params = {}) => {
  // Log the parameters being sent to the API
  console.log("Product service getProducts params:", params);

  // Create a copy of params to avoid modifying the original
  const apiParams = { ...params };

  // Remove legacy parameters that are no longer needed
  delete apiParams.includeVariationSummary;
  delete apiParams.includeVariations;
  delete apiParams.includeVariationAttributes;
  delete apiParams.parentIdOnly;

  // Handle attribute filters - ensure they're properly formatted for the API
  if (apiParams.attributes && typeof apiParams.attributes === "string") {
    try {
      // The API expects a JSON string, so we ensure it's properly formatted
      JSON.parse(apiParams.attributes); // Just to validate it's a proper JSON string
      console.log("Valid attributes JSON:", apiParams.attributes);
    } catch (error) {
      console.error("Error parsing attributes JSON:", error);
      // If there's an error parsing, remove the attributes parameter
      delete apiParams.attributes;
    }
  }

  try {
    const response = await axiosInstance.get(PRODUCTS_URL, {
      params: apiParams,
    });

    // Debug response structure
    console.log("API response structure:", {
      productsCount: response.data.products?.length || 0,
      paginationData: response.data.pagination,
      priceRange: response.data.priceRange,
      weightRange: response.data.weightRange,
      variableProductsCount:
        response.data.products?.filter((p) => p.productType === "variable")
          .length || 0,
      simpleProductsCount:
        response.data.products?.filter((p) => p.productType === "simple")
          .length || 0,
    });

    // Ensure pagination data exists
    const pagination = response.data.pagination || {};
    if (!pagination.totalItems && response.data.products) {
      pagination.totalItems = response.data.products.length;
    }
    if (!pagination.totalPages && pagination.totalItems) {
      pagination.totalPages = Math.ceil(
        pagination.totalItems / (params.limit || 12)
      );
    }
    if (!pagination.page) {
      pagination.page = params.page || 1;
    }

    response.data.pagination = pagination;
    return response.data;
  } catch (error) {
    console.error("Error fetching products:", error);
    // Return empty results rather than throwing
    return {
      products: [],
      pagination: {
        totalItems: 0,
        totalPages: 0,
        page: parseInt(params.page) || 1,
      },
      priceRange: { min: 0, max: 1000 },
      weightRange: { min: 0, max: 0 },
    };
  }
};

/**
 * Get product by ID
 * @param {string} id - Product ID
 * @param {boolean} includeVariations - Whether to include product variations (defaults to true)
 * @returns {Promise<Object>} Product data
 */
const getProductById = async (id, includeVariations = true) => {
  try {
    const params = includeVariations ? { includeVariations: "true" } : {};
    console.log(`Fetching product ${id} with params:`, params);
    const response = await axiosInstance.get(`${PRODUCTS_URL}/${id}`, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error getting product by ID:", error);
    throw error;
  }
};

/**
 * Create a new product
 * @param {Object} productData - Product data to create
 * @returns {Object} Created product data
 */
const createProduct = async (productData) => {
  const response = await axiosInstance.post(PRODUCTS_URL, productData);
  return response.data;
};

/**
 * Update product
 * @param {string} id - Product ID
 * @param {Object} productData - Product data to update
 * @returns {Object} Updated product data
 */
const updateProduct = async (id, productData) => {
  const response = await axiosInstance.put(
    `${PRODUCTS_URL}/${id}`,
    productData
  );
  return response.data;
};

/**
 * Delete product
 * @param {string} id - Product ID
 * @returns {Object} Success message
 */
const deleteProduct = async (id) => {
  const response = await axiosInstance.delete(`${PRODUCTS_URL}/${id}`);
  return response.data;
};

/**
 * Upload product images
 * @param {string} productId - Product ID
 * @param {FormData} formData - Form data with images
 * @returns {Object} Upload response with image data
 */
const uploadProductImages = async (productId, formData) => {
  const response = await axiosInstance.post(
    `${PRODUCTS_URL}/${productId}/images`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

/**
 * Get product images
 * @param {string} id - Product ID
 * @returns {Array} Product images
 */
const getProductImages = async (id) => {
  const response = await axiosInstance.get(`${PRODUCTS_URL}/${id}/images`);
  return response.data;
};

/**
 * Update product image details
 * @param {string} productId - Product ID
 * @param {string} imageId - Image ID
 * @param {Object} imageData - Image data to update
 * @returns {Object} Updated image data
 */
const updateProductImage = async (productId, imageId, imageData) => {
  const response = await axiosInstance.put(
    `${PRODUCTS_URL}/${productId}/images/${imageId}`,
    imageData
  );
  return response.data;
};

/**
 * Delete product image
 * @param {string} productId - Product ID
 * @param {string} imageId - Image ID
 * @returns {Object} Success message
 */
const deleteProductImage = async (productId, imageId) => {
  const response = await axiosInstance.delete(
    `${PRODUCTS_URL}/${productId}/images/${imageId}`
  );
  return response.data;
};

/**
 * Set a product image as primary
 * @param {string} productId - Product ID
 * @param {string} imageId - Image ID
 * @returns {Object} Updated image data
 */
const setPrimaryImage = async (productId, imageId) => {
  const response = await axiosInstance.put(
    `${PRODUCTS_URL}/${productId}/images/${imageId}/primary`
  );
  return response.data;
};

/**
 * Reorder product images
 * @param {string} productId - Product ID
 * @param {Array} imageOrder - Array of image IDs in the desired order
 * @returns {Array} Updated product images
 */
const reorderProductImages = async (productId, imageOrder) => {
  const response = await axiosInstance.put(
    `${PRODUCTS_URL}/${productId}/images/reorder`,
    { imageOrder }
  );
  return response.data;
};

/**
 * Get product attributes
 * @param {string} id - Product ID
 * @returns {Array} Product attributes
 */
const getProductAttributes = async (id) => {
  // If no id is provided, get all available attributes instead
  // This is used for the Create Product page
  if (!id) {
    const response = await axiosInstance.get("/attributes");
    return response.data;
  }

  const response = await axiosInstance.get(`${PRODUCTS_URL}/${id}/attributes`);
  return response.data;
};

/**
 * Update product attributes
 * @param {string} id - Product ID
 * @param {Array} attributes - Product attributes
 * @returns {Object} Updated product with attributes
 */
const updateProductAttributes = async (id, attributes) => {
  const response = await axiosInstance.put(`${PRODUCTS_URL}/${id}/attributes`, {
    attributes,
  });
  return response.data;
};

/**
 * Generate variations for a variable product (legacy endpoint)
 * @param {string} id - Product ID
 * @param {Array} attributeIds - Array of attribute IDs to use for variations
 * @returns {Object} Generated variations data
 */
const generateProductVariations = async (id, attributeIds) => {
  const response = await axiosInstance.post(
    `${PRODUCTS_URL}/${id}/generate-variations`,
    {
      attributeIds,
    }
  );
  return response.data;
};

/**
 * Create a variation for a variable product
 * @param {string} id - Parent product ID
 * @param {Object} variationData - Variation data
 * @returns {Object} Created variation data
 */
const createVariation = async (id, variationData) => {
  const response = await axiosInstance.post(
    `${PRODUCTS_URL}/${id}/variations`,
    variationData
  );
  return response.data;
};

/**
 * Get variations for a variable product
 * @param {string} id - Product ID
 * @returns {Array} Product variations
 */
const getProductVariations = async (id) => {
  const response = await axiosInstance.get(`${PRODUCTS_URL}/${id}/variations`);
  return response.data;
};

/**
 * Update a product variation
 * @param {string} productId - Parent product ID
 * @param {string} variationId - Variation ID
 * @param {Object} variationData - Updated variation data
 * @returns {Object} Updated variation data
 */
const updateVariation = async (productId, variationId, variationData) => {
  const response = await axiosInstance.put(
    `${PRODUCTS_URL}/${productId}/variations/${variationId}`,
    variationData
  );
  return response.data;
};

/**
 * Delete a product variation
 * @param {string} productId - Parent product ID
 * @param {string} variationId - Variation ID
 * @returns {Object} Success message
 */
const deleteVariation = async (productId, variationId) => {
  const response = await axiosInstance.delete(
    `${PRODUCTS_URL}/${productId}/variations/${variationId}`
  );
  return response.data;
};

/**
 * Generate variations for a product based on selected attributes
 * @param {number|string} productId - The ID of the product
 * @param {Object} data - The variation generation data
 * @param {Array} data.attributeIds - Array of attribute IDs to use for variation generation
 * @returns {Promise<Array>} - Promise that resolves to an array of generated variations
 */
const generateVariations = async (productId, data) => {
  try {
    console.log(`Generating variations for product ${productId}:`, data);

    // Ensure the endpoint exists in API
    const endpoint = `${PRODUCTS_URL}/${productId}/variations/generate`;
    console.log(`Calling API endpoint: ${endpoint}`);

    const response = await axiosInstance.post(endpoint, data);
    console.log("Variation generation response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error generating variations:", error.response || error);
    throw error;
  }
};

const productService = {
  getProducts,
  getProductById,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadProductImages,
  getProductImages,
  updateProductImage,
  deleteProductImage,
  setPrimaryImage,
  reorderProductImages,
  getProductAttributes,
  updateProductAttributes,
  generateProductVariations,
  createVariation,
  getProductVariations,
  updateVariation,
  deleteVariation,
  generateVariations,
};

export default productService;
